(() => {
  const PACK_CONTRACTS = {
    'height-veda-capsule': { prices: [49900, 89900, 119900], compare: 389800, discountedPacks: true },
    'height-veda-powder': { prices: [69900, 119900, 149900], compare: 199900, discountedPacks: true },
    'height-veda-combo-offer': { prices: [99900, 199800, 299700], compare: 589700, discountedPacks: false },
    'height-veda-complete-combo': { prices: [99900, 199800, 299700], compare: 589700, discountedPacks: false }
  };

  const getPackConfig = (handle, packOptionIndex = -1) => ({
    handle,
    enabled: Object.prototype.hasOwnProperty.call(PACK_CONTRACTS, handle),
    packOptionIndex: Number.isInteger(Number(packOptionIndex)) ? Number(packOptionIndex) : -1,
    ...(PACK_CONTRACTS[handle] || {})
  });

  const isQuantityPackMode = (config) => Boolean(config?.enabled && config.packOptionIndex < 0);

  const packCountFromOption = (value) => {
    if (value === '1 Pack' || value === 'Pack of 1') return 1;
    if (value === '2 Packs' || value === 'Pack of 2') return 2;
    if (value === '3 Packs' || value === 'Pack of 3') return 3;
    return 0;
  };

  const packCountForVariant = (variant, config) => {
    if (!variant) return 0;
    if (!config.enabled || config.packOptionIndex < 0) return 1;
    return packCountFromOption(variant.options?.[config.packOptionIndex]);
  };

  const getPackPrice = (config, count) => (
    config.enabled && Number.isInteger(Number(count)) && Number(count) >= 1 && Number(count) <= 3
      ? config.prices[Number(count) - 1]
      : null
  );

  const quantityAllows = (variant, quantity = 1) => {
    if (!variant) return false;
    const requested = Number(quantity);
    const minimum = Number(variant.min ?? 1);
    const increment = Number(variant.increment ?? 1);
    const maximum = variant.max === null || typeof variant.max === 'undefined' ? null : Number(variant.max);
    return Number.isInteger(requested) && requested >= 1
      && Number.isInteger(minimum) && minimum >= 0 && requested >= minimum
      && Number.isInteger(increment) && increment > 0 && (requested - minimum) % increment === 0
      && (maximum === null || (Number.isInteger(maximum) && maximum >= requested));
  };

  const quantityAllowsOne = (variant) => quantityAllows(variant, 1);

  const isConfiguredPackVariant = (variant, count, config) => {
    if (!variant || !variant.id || !config.enabled) return false;
    if (isQuantityPackMode(config)) {
      return Number(variant.price) === getPackPrice(config, 1)
        && getPackPrice(config, count) !== null
        && quantityAllows(variant, count);
    }
    return packCountForVariant(variant, config) === Number(count)
      && Number(variant.price) === getPackPrice(config, count)
      && quantityAllowsOne(variant);
  };

  const isValidPackVariant = (variant, count, config) => (
    isConfiguredPackVariant(variant, count, config) && variant.available === true
  );

  const isVariantPurchasable = (variant, config, count = 1) => (
    config.enabled
      ? isValidPackVariant(variant, count, config)
      : Boolean(variant && variant.id && variant.available === true && quantityAllowsOne(variant))
  );

  const matchesOtherOptions = (candidate, selected, config) => {
    if (!candidate || !selected) return false;
    if (!Array.isArray(candidate.options) || !Array.isArray(selected.options)) return String(candidate.id) === String(selected.id);
    const options = candidate.options;
    const selectedOptions = selected.options;
    return options.length === selectedOptions.length
      && options.every((value, index) => index === config.packOptionIndex || value === selectedOptions[index]);
  };

  const findPackVariant = (variants, count, selected, config) => (
    variants.find((variant) => matchesOtherOptions(variant, selected, config) && isValidPackVariant(variant, count, config)) || null
  );

  const formatINR = (cents, forceDecimals = false) => {
    const amount = Number(cents) / 100;
    const decimals = forceDecimals || !Number.isInteger(amount) ? 2 : 0;
    const formatted = new Intl.NumberFormat('en-IN', { minimumFractionDigits: decimals, maximumFractionDigits: decimals }).format(amount);
    return `₹${formatted}${forceDecimals ? '' : '/-'}`;
  };

  const getPackTilePricing = (config, count) => {
    const price = getPackPrice(config, count);
    if (price === null) return null;
    const compareAtPrice = config.discountedPacks && Number(count) > 1 ? config.prices[0] * Number(count) : 0;
    const saving = Math.max(0, compareAtPrice - price);
    return {
      price,
      priceLabel: formatINR(price),
      compareAtPrice,
      compareAtPriceLabel: formatINR(compareAtPrice),
      saving,
      savingLabel: `Save ${formatINR(saving)}`,
      unitLabel: `${formatINR(Math.round(price / Number(count)), true)} per pack`
    };
  };

  const getVariantPricing = (variant, config, selectedPackCount = 1) => {
    if (!variant) return null;
    const basePrice = Number(variant.price);
    const quantityMode = isQuantityPackMode(config);
    const packCount = quantityMode ? Number(selectedPackCount) : packCountForVariant(variant, config);
    const contractPrice = getPackPrice(config, packCount);
    const hasContractPrice = config.enabled && packCount > 0 && contractPrice !== null
      && (quantityMode ? basePrice === getPackPrice(config, 1) : basePrice === contractPrice);
    const price = hasContractPrice && quantityMode ? contractPrice : basePrice;
    let compareAtPrice = Number(variant.compareAtPrice || 0);
    if (hasContractPrice) {
      if (packCount === 1 && compareAtPrice <= price) compareAtPrice = config.compare;
      else if (packCount > 1) compareAtPrice = config.discountedPacks ? config.prices[0] * packCount : 0;
    }
    const hasCompare = compareAtPrice > price;
    const saving = hasCompare ? compareAtPrice - price : 0;
    return {
      price,
      priceLabel: hasContractPrice ? formatINR(price) : variant.priceLabel || formatINR(price),
      compareAtPrice,
      compareAtPriceLabel: hasContractPrice ? formatINR(compareAtPrice) : variant.compareAtPriceLabel || formatINR(compareAtPrice),
      hasCompare,
      saving,
      savingLabel: saving ? !config.enabled && variant.savingLabel ? variant.savingLabel : `Save ${formatINR(saving)}` : '',
      discountPercent: hasCompare ? Math.round(saving * 100 / compareAtPrice) : 0,
      packCount,
      packLabel: config.enabled ? packCount ? `${packCount} ${packCount === 1 ? 'Pack' : 'Packs'}` : 'Pack unavailable' : variant.title || 'Selected option'
    };
  };

  // Pure helpers also run in Node; loading this asset there does not initialise a browser.
  if (typeof module === 'object' && module.exports) {
    module.exports = { getPackConfig, isQuantityPackMode, packCountFromOption, packCountForVariant, getPackPrice, quantityAllows, quantityAllowsOne, isConfiguredPackVariant, isValidPackVariant, isVariantPurchasable, matchesOtherOptions, findPackVariant, formatINR, getPackTilePricing, getVariantPricing };
  }
  if (typeof document === 'undefined') return;
  if (window.__hvProductPageAssetReady) return;
  window.__hvProductPageAssetReady = true;

  const qs = (selector, root = document) => root.querySelector(selector);
  const qsa = (selector, root = document) => Array.from(root.querySelectorAll(selector));

  const announce = (message) => {
    let liveRegion = document.querySelector('[data-hv-product-live-region]');
    if (!liveRegion) {
      liveRegion = document.createElement('div');
      liveRegion.dataset.hvProductLiveRegion = '';
      liveRegion.setAttribute('role', 'status');
      liveRegion.setAttribute('aria-live', 'polite');
      liveRegion.style.position = 'fixed';
      liveRegion.style.width = '1px';
      liveRegion.style.height = '1px';
      liveRegion.style.overflow = 'hidden';
      liveRegion.style.clip = 'rect(0 0 0 0)';
      document.body.appendChild(liveRegion);
    }
    liveRegion.textContent = message;
  };

  const parseVariants = (root) => {
    const data = qs('[data-hv-product-variants]', root);
    if (!data) return [];
    try {
      const variants = JSON.parse(data.textContent || '[]');
      return Array.isArray(variants) ? variants : [];
    } catch (error) {
      return [];
    }
  };

  const initCountdown = (root) => {
    const countdown = qs('[data-hv-product-countdown]', root);
    if (!countdown || countdown.dataset.hvProductReady === 'true') return;
    countdown.dataset.hvProductReady = 'true';

    const duration = Number(countdown.dataset.durationSeconds || 28800) * 1000;
    const storedEnd = Number(countdown.dataset.endAt || 0);
    const end = storedEnd > Date.now() ? storedEnd : Date.now() + duration;
    countdown.dataset.endAt = String(end);

    const hours = qs('[data-hv-product-hours]', countdown);
    const minutes = qs('[data-hv-product-minutes]', countdown);
    const seconds = qs('[data-hv-product-seconds]', countdown);
    const pad = (value) => String(value).padStart(2, '0');

    const update = () => {
      const remaining = Math.max(0, end - Date.now());
      const totalSeconds = Math.floor(remaining / 1000);
      const hourValue = Math.floor(totalSeconds / 3600);
      const minuteValue = Math.floor((totalSeconds % 3600) / 60);
      const secondValue = totalSeconds % 60;
      if (hours) hours.textContent = pad(hourValue);
      if (minutes) minutes.textContent = pad(minuteValue);
      if (seconds) seconds.textContent = pad(secondValue);
      if (remaining <= 0) {
        countdown.setAttribute('aria-label', 'Offer ended');
        return false;
      }
      countdown.setAttribute('aria-label', `Offer ends in ${hourValue} hours, ${minuteValue} minutes, ${secondValue} seconds`);
      return true;
    };

    update();
    const timer = window.setInterval(() => {
      if (!update()) window.clearInterval(timer);
    }, 1000);
  };

  const initGallery = (root) => {
    const gallery = qs('[data-hv-product-gallery]', root);
    if (!gallery || gallery.dataset.hvProductReady === 'true') return;
    gallery.dataset.hvProductReady = 'true';

    const mainImage = qs('[data-hv-product-main-image]', gallery);
    const thumbnails = qsa('[data-hv-product-thumbnail]', gallery);
    const status = qs('[data-hv-product-gallery-status]', gallery);
    const previous = qs('[data-hv-product-gallery-previous]', gallery);
    const next = qs('[data-hv-product-gallery-next]', gallery);
    if (!mainImage || !thumbnails.length) return;

    let currentIndex = thumbnails.findIndex((thumbnail) => thumbnail.getAttribute('aria-current') === 'true');
    if (currentIndex < 0) currentIndex = 0;

    const updateStatus = (index) => {
      if (status) status.textContent = `Image ${index + 1} of ${thumbnails.length}`;
    };

    const setCurrentThumbnail = (index) => {
      thumbnails.forEach((thumbnail, thumbnailIndex) => {
        thumbnail.setAttribute('aria-current', String(thumbnailIndex === index));
      });
      currentIndex = index;
      updateStatus(index);
    };

    const showImage = (image, index = -1) => {
      if (!image) return;
      mainImage.src = image.src || '';
      if (image.srcset) mainImage.srcset = image.srcset;
      else mainImage.removeAttribute('srcset');
      if (image.alt) mainImage.alt = image.alt;
      if (image.width) mainImage.width = image.width;
      if (image.height) mainImage.height = image.height;
      if (index >= 0 && index < thumbnails.length) setCurrentThumbnail(index);
      else {
        thumbnails.forEach((thumbnail) => {
          thumbnail.setAttribute('aria-current', String(String(thumbnail.dataset.hvProductImageId) === String(image.id)));
        });
      }
    };

    const imageFromThumbnail = (thumbnail) => ({
      id: thumbnail.dataset.hvProductImageId,
      src: thumbnail.dataset.hvProductImageSrc,
      srcset: thumbnail.dataset.hvProductImageSrcset,
      alt: thumbnail.dataset.hvProductImageAlt || '',
      width: Number(thumbnail.dataset.hvProductImageWidth || 0),
      height: Number(thumbnail.dataset.hvProductImageHeight || 0)
    });

    const thumbnailStrip = thumbnails[0]?.parentElement;
    const keepThumbnailVisible = (thumbnail) => {
      if (!thumbnailStrip || !thumbnail || typeof thumbnailStrip.scrollTo !== 'function') return;
      const left = Math.max(0, thumbnail.offsetLeft - (thumbnailStrip.clientWidth - thumbnail.offsetWidth) / 2);
      thumbnailStrip.scrollTo({ left, behavior: 'smooth' });
    };

    const move = (offset) => {
      const nextIndex = (currentIndex + offset + thumbnails.length) % thumbnails.length;
      showImage(imageFromThumbnail(thumbnails[nextIndex]), nextIndex);
      keepThumbnailVisible(thumbnails[nextIndex]);
    };

    const galleryAutoplayDelay = 2600;
    const reducedMotionQuery = typeof window.matchMedia === 'function'
      ? window.matchMedia('(prefers-reduced-motion: reduce)')
      : null;
    let galleryAutoplayTimer = 0;
    let galleryResumeTimer = 0;
    let pointerWithin = false;
    let focusWithin = false;
    let interactionPaused = false;
    let pageVisible = document.visibilityState !== 'hidden';
    const canAutoplay = () => (
      thumbnails.length > 1
      && !pointerWithin
      && !focusWithin
      && !interactionPaused
      && pageVisible
      && !reducedMotionQuery?.matches
    );
    const stopAutoplay = () => {
      if (!galleryAutoplayTimer) return;
      window.clearInterval(galleryAutoplayTimer);
      galleryAutoplayTimer = 0;
    };
    const startAutoplay = () => {
      if (galleryAutoplayTimer || !canAutoplay()) return;
      galleryAutoplayTimer = window.setInterval(() => {
        if (!canAutoplay()) {
          stopAutoplay();
          return;
        }
        move(1);
      }, galleryAutoplayDelay);
    };
    const pauseForInteraction = () => {
      interactionPaused = true;
      stopAutoplay();
      if (galleryResumeTimer) window.clearTimeout(galleryResumeTimer);
      galleryResumeTimer = window.setTimeout(() => {
        galleryResumeTimer = 0;
        interactionPaused = false;
        startAutoplay();
      }, galleryAutoplayDelay);
    };

    thumbnails.forEach((thumbnail, index) => {
      thumbnail.addEventListener('click', () => {
        pauseForInteraction();
        showImage(imageFromThumbnail(thumbnail), index);
      });
    });
    if (previous) previous.addEventListener('click', () => { pauseForInteraction(); move(-1); });
    if (next) next.addEventListener('click', () => { pauseForInteraction(); move(1); });

    const frame = qs('.hv-product-page__gallery-frame', gallery);
    if (frame) {
      frame.addEventListener('pointerenter', () => {
        pointerWithin = true;
        stopAutoplay();
      });
      frame.addEventListener('pointerleave', () => {
        pointerWithin = false;
        startAutoplay();
      });
      frame.addEventListener('pointerdown', pauseForInteraction, { passive: true });
      frame.addEventListener('focusin', () => {
        focusWithin = true;
        stopAutoplay();
      });
      frame.addEventListener('focusout', () => {
        window.setTimeout(() => {
          focusWithin = frame.contains(document.activeElement);
          if (!focusWithin) startAutoplay();
        }, 0);
      });
      frame.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowLeft') {
          event.preventDefault();
          pauseForInteraction();
          move(-1);
        }
        if (event.key === 'ArrowRight') {
          event.preventDefault();
          pauseForInteraction();
          move(1);
        }
      });
      let touchStartX = 0;
      frame.addEventListener('touchstart', (event) => {
        pauseForInteraction();
        touchStartX = event.changedTouches[0]?.screenX || 0;
      }, { passive: true });
      frame.addEventListener('touchend', (event) => {
        const touchEndX = event.changedTouches[0]?.screenX || 0;
        const difference = touchStartX - touchEndX;
        if (Math.abs(difference) > 45) move(difference > 0 ? 1 : -1);
      }, { passive: true });
    }
    const handleVisibilityChange = () => {
      pageVisible = document.visibilityState !== 'hidden';
      if (pageVisible) startAutoplay();
      else stopAutoplay();
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    if (reducedMotionQuery) {
      const handleReducedMotionChange = () => {
        if (reducedMotionQuery.matches) stopAutoplay();
        else startAutoplay();
      };
      if (typeof reducedMotionQuery.addEventListener === 'function') reducedMotionQuery.addEventListener('change', handleReducedMotionChange);
      else if (typeof reducedMotionQuery.addListener === 'function') reducedMotionQuery.addListener(handleReducedMotionChange);
    }
    updateStatus(currentIndex);
    startAutoplay();
  };

  const optionCarouselInstances = new WeakMap();
  const optionCarouselAutoplayDelay = 2200;

  const initOptionCarousel = (root) => {
    const carousel = qs('[data-hv-option-carousel]', root);
    if (!carousel || optionCarouselInstances.has(carousel) || carousel.dataset.hvOptionReady === 'true') return;

    const viewport = qs('[data-hv-option-viewport]', carousel);
    const track = qs('[data-hv-option-track]', carousel);
    const cards = qsa('.hv-product-page__option-card', track || carousel);
    if (!viewport || !track || cards.length < 2) return;

    const controls = qs('.hv-product-page__option-controls', carousel);
    const previous = qs('[data-hv-option-prev]', carousel);
    const next = qs('[data-hv-option-next]', carousel);
    const dots = qsa('[data-hv-option-dot]', carousel);
    const toggle = qs('[data-hv-option-toggle]', carousel);
    const reducedMotionQuery = typeof window.matchMedia === 'function'
      ? window.matchMedia('(prefers-reduced-motion: reduce)')
      : null;

    let autoplayTimer = 0;
    let interactionResumeTimer = 0;
    let focusCheckTimer = 0;
    let resizeObserver = null;
    let intersectionObserver = null;
    let destroyed = false;
    let hasOverflow = false;
    let positions = [];
    let currentIndex = 0;
    let interactionPaused = false;
    let userPaused = toggle?.getAttribute('aria-pressed') === 'true';
    let pointerWithin = false;
    let focusWithin = carousel.contains(document.activeElement);
    let pageVisible = document.visibilityState !== 'hidden';
    let inView = true;
    let reducedMotion = Boolean(reducedMotionQuery?.matches);
    const cleanupListeners = [];

    const listen = (target, type, handler, options) => {
      if (!target || typeof target.addEventListener !== 'function') return;
      target.addEventListener(type, handler, options);
      cleanupListeners.push(() => target.removeEventListener(type, handler, options));
    };

    const clamp = (value, minimum, maximum) => Math.min(Math.max(value, minimum), maximum);
    const normalizeIndex = (index) => (index + cards.length) % cards.length;
    const updateDots = () => {
      dots.forEach((dot, index) => dot.setAttribute('aria-current', String(index === currentIndex)));
    };
    const updateToggle = () => {
      if (!toggle) return;
      const paused = userPaused;
      toggle.textContent = paused ? 'Resume' : 'Pause';
      toggle.setAttribute('aria-label', `${paused ? 'Resume' : 'Pause'} product format carousel`);
      toggle.setAttribute('aria-pressed', String(paused));
    };
    const stopAutoplay = () => {
      if (!autoplayTimer) return;
      window.clearInterval(autoplayTimer);
      autoplayTimer = 0;
    };
    const canAutoplay = () => (
      hasOverflow
      && !interactionPaused
      && !userPaused
      && !pointerWithin
      && !focusWithin
      && pageVisible
      && inView
      && !reducedMotion
    );
    const startAutoplay = () => {
      if (autoplayTimer || !canAutoplay()) return;
      autoplayTimer = window.setInterval(() => {
        if (!canAutoplay()) {
          stopAutoplay();
          return;
        }
        moveTo(currentIndex + 1, false);
      }, optionCarouselAutoplayDelay);
    };
    const clearInteractionPause = () => {
      interactionPaused = false;
      if (interactionResumeTimer) {
        window.clearTimeout(interactionResumeTimer);
        interactionResumeTimer = 0;
      }
    };
    const pauseForInteraction = () => {
      interactionPaused = true;
      stopAutoplay();
      if (interactionResumeTimer) window.clearTimeout(interactionResumeTimer);
      interactionResumeTimer = window.setTimeout(() => {
        interactionResumeTimer = 0;
        interactionPaused = false;
        startAutoplay();
      }, optionCarouselAutoplayDelay);
    };
    const getMaxScroll = () => Math.max(0, viewport.scrollWidth - viewport.clientWidth);
    const getCardScrollPosition = (card, index) => {
      const viewportRect = viewport.getBoundingClientRect();
      const cardRect = card.getBoundingClientRect();
      const hasLayout = Boolean(cardRect.width || cardRect.height || viewportRect.width || viewportRect.height);
      const fallbackPosition = card.offsetLeft - (track.offsetLeft || 0);
      const evenlySpacedPosition = getMaxScroll() * index / Math.max(1, cards.length - 1);
      const position = hasLayout
        ? cardRect.left - viewportRect.left + viewport.scrollLeft
        : fallbackPosition || evenlySpacedPosition;
      return clamp(position, 0, getMaxScroll());
    };
    const getNearestIndex = () => {
      if (!positions.length) return 0;
      return positions.reduce((nearest, position, index) => (
        Math.abs(position - viewport.scrollLeft) < Math.abs(positions[nearest] - viewport.scrollLeft)
          ? index
          : nearest
      ), 0);
    };
    const updateCurrentFromScroll = () => {
      const nearestIndex = getNearestIndex();
      if (nearestIndex === currentIndex) return;
      currentIndex = nearestIndex;
      updateDots();
    };
    const refreshLayout = () => {
      if (destroyed) return;
      const maxScroll = getMaxScroll();
      positions = cards.map(getCardScrollPosition);
      hasOverflow = maxScroll > 1;
      if (hasOverflow) {
        currentIndex = getNearestIndex();
        if (controls) controls.hidden = false;
        startAutoplay();
      } else {
        currentIndex = 0;
        if (controls) controls.hidden = true;
        stopAutoplay();
      }
      updateDots();
    };
    const scrollToIndex = (index) => {
      const target = positions[index] || 0;
      const behavior = reducedMotion ? 'auto' : 'smooth';
      if (typeof viewport.scrollTo === 'function') viewport.scrollTo({ left: target, behavior });
      else viewport.scrollLeft = target;
    };
    const moveTo = (index, isManual) => {
      if (!hasOverflow) return;
      if (isManual) pauseForInteraction();
      currentIndex = normalizeIndex(index);
      updateDots();
      scrollToIndex(currentIndex);
    };
    const handlePrevious = () => moveTo(currentIndex - 1, true);
    const handleNext = () => moveTo(currentIndex + 1, true);
    const handleDot = (event) => {
      const index = Number(event.currentTarget.dataset.index || event.currentTarget.dataset.hvOptionDot);
      if (Number.isInteger(index)) moveTo(index, true);
    };
    const handleToggle = () => {
      clearInteractionPause();
      userPaused = !userPaused;
      toggle.setAttribute('aria-pressed', String(userPaused));
      updateToggle();
      if (userPaused) stopAutoplay();
      else startAutoplay();
    };
    const handleKeydown = (event) => {
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        handlePrevious();
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        handleNext();
      }
    };
    const handleFocusIn = () => {
      focusWithin = true;
      stopAutoplay();
    };
    const handleFocusOut = () => {
      if (focusCheckTimer) {
        window.clearTimeout(focusCheckTimer);
        focusCheckTimer = 0;
      }
      focusCheckTimer = window.setTimeout(() => {
        focusCheckTimer = 0;
        if (destroyed) return;
        focusWithin = carousel.contains(document.activeElement);
        if (!focusWithin) startAutoplay();
      }, 0);
    };
    const handlePointerInteraction = () => pauseForInteraction();
    const handlePointerEnter = (event) => {
      if (event.type === 'pointerenter' && event.pointerType === 'touch') return;
      pointerWithin = true;
      stopAutoplay();
    };
    const handlePointerLeave = (event) => {
      if (event.type === 'pointerleave' && event.pointerType === 'touch') return;
      pointerWithin = false;
      startAutoplay();
    };
    const handleScroll = () => updateCurrentFromScroll();
    const handleVisibilityChange = () => {
      pageVisible = document.visibilityState !== 'hidden';
      if (pageVisible) startAutoplay();
      else stopAutoplay();
    };
    const handleReducedMotionChange = (event) => {
      reducedMotion = Boolean(event.matches);
      if (reducedMotion) stopAutoplay();
      else startAutoplay();
    };
    const handleIntersection = (entries) => {
      const entry = entries[0];
      inView = Boolean(entry?.isIntersecting && (typeof entry.intersectionRatio !== 'number' || entry.intersectionRatio > 0));
      if (inView) startAutoplay();
      else stopAutoplay();
    };

    carousel.dataset.hvOptionReady = 'true';
    updateToggle();
    listen(previous, 'click', handlePrevious);
    listen(next, 'click', handleNext);
    dots.forEach((dot) => listen(dot, 'click', handleDot));
    listen(toggle, 'click', handleToggle);
    listen(carousel, 'keydown', handleKeydown);
    listen(carousel, 'focusin', handleFocusIn);
    listen(carousel, 'focusout', handleFocusOut);
    listen(carousel, 'pointerenter', handlePointerEnter);
    listen(carousel, 'pointerleave', handlePointerLeave);
    listen(carousel, 'pointerdown', handlePointerInteraction);
    listen(carousel, 'touchstart', handlePointerInteraction, { passive: true });
    listen(carousel, 'wheel', handlePointerInteraction, { passive: true });
    listen(viewport, 'scroll', handleScroll, { passive: true });
    listen(document, 'visibilitychange', handleVisibilityChange);
    listen(window, 'resize', refreshLayout);

    if (reducedMotionQuery) {
      if (typeof reducedMotionQuery.addEventListener === 'function') {
        reducedMotionQuery.addEventListener('change', handleReducedMotionChange);
        cleanupListeners.push(() => reducedMotionQuery.removeEventListener('change', handleReducedMotionChange));
      } else if (typeof reducedMotionQuery.addListener === 'function') {
        reducedMotionQuery.addListener(handleReducedMotionChange);
        cleanupListeners.push(() => reducedMotionQuery.removeListener(handleReducedMotionChange));
      }
    }

    if (typeof ResizeObserver === 'function') {
      resizeObserver = new ResizeObserver(refreshLayout);
      resizeObserver.observe(viewport);
      resizeObserver.observe(track);
    }
    if (typeof IntersectionObserver === 'function') {
      intersectionObserver = new IntersectionObserver(handleIntersection, { threshold: 0.01 });
      intersectionObserver.observe(carousel);
    }

    const destroy = () => {
      if (destroyed) return;
      destroyed = true;
      stopAutoplay();
      clearInteractionPause();
      if (focusCheckTimer) {
        window.clearTimeout(focusCheckTimer);
        focusCheckTimer = 0;
      }
      cleanupListeners.forEach((cleanup) => cleanup());
      if (resizeObserver) resizeObserver.disconnect();
      if (intersectionObserver) intersectionObserver.disconnect();
      delete carousel.dataset.hvOptionReady;
      optionCarouselInstances.delete(carousel);
    };

    optionCarouselInstances.set(carousel, { destroy });
    refreshLayout();
  };

  const initVariantForm = (root) => {
    const variantInput = qs('[data-hv-product-variant]', root);
    const form = qs('[data-hv-product-variant-form]', root) || variantInput?.form || qs('form.hv-product-page__form', root);
    const variants = parseVariants(root);
    if (!form || !variantInput || !variants.length || form.dataset.hvProductReady === 'true') return;
    form.dataset.hvProductReady = 'true';
    const config = getPackConfig(root.dataset.hvProductHandle || '', Number(root.dataset.hvPackOptionIndex ?? -1));
    const quantityPackMode = isQuantityPackMode(config);
    const packSelector = qs('[data-hv-pack-selector]', form);
    const packInputs = qsa('[data-hv-pack-count]', form);
    const variantField = qs('[data-hv-product-variant-field]', form);
    const findSelectedVariant = () => variants.find((item) => String(item.id) === String(variantInput.value)) || null;
    const getSelectedPackCount = () => {
      if (!quantityPackMode) return packCountForVariant(findSelectedVariant(), config);
      const checked = packInputs.find((input) => input.checked);
      const count = Number(checked?.dataset.hvPackCount || root.dataset.hvPackCount || 1);
      return Number.isInteger(count) && count >= 1 && count <= 3 ? count : 1;
    };
    const packProperties = {
      size: qs('[data-hv-pack-property="size"]', form),
      total: qs('[data-hv-pack-property="total"]', form),
      count: qs('[data-hv-pack-property="count"]', form)
    };

    const gallery = qs('[data-hv-product-gallery]', root);
    const mainImage = gallery ? qs('[data-hv-product-main-image]', gallery) : null;
    const thumbnails = gallery ? qsa('[data-hv-product-thumbnail]', gallery) : [];
    const defaultImage = mainImage ? {
      id: thumbnails.find((thumbnail) => thumbnail.getAttribute('aria-current') === 'true')?.dataset.hvProductImageId || '',
      src: mainImage.currentSrc || mainImage.src,
      srcset: mainImage.getAttribute('srcset') || '',
      alt: mainImage.alt || '',
      width: mainImage.width,
      height: mainImage.height
    } : null;
    const quantity = qs('[data-hv-product-quantity]', form);
    const galleryStatus = qs('[data-hv-product-gallery-status]', root);

    const refreshPackOptions = (selected) => {
      const selectedCount = quantityPackMode ? getSelectedPackCount() : packCountForVariant(selected, config);
      let needsSetup = false;
      packInputs.forEach((input) => {
        const count = Number(input.dataset.hvPackCount);
        const match = findPackVariant(variants, count, selected, config);
        const tile = input.closest('[data-hv-pack-option]');
        const pricing = getPackTilePricing(config, count);
        input.disabled = !match;
        input.checked = count === selectedCount;
        input.dataset.hvPackVariantId = match ? String(match.id) : '';
        if (tile) {
          tile.dataset.hvPackAvailable = String(Boolean(match));
          const status = qs('[data-hv-pack-status]', tile);
          if (status) status.hidden = Boolean(match);
          if (pricing) {
            const tilePrice = qs('[data-hv-pack-price]', tile);
            const compare = qs('[data-hv-pack-compare]', tile);
            const saving = qs('[data-hv-pack-saving]', tile);
            const unit = qs('[data-hv-pack-unit]', tile);
            if (tilePrice) tilePrice.textContent = pricing.priceLabel;
            if (compare) {
              compare.textContent = pricing.compareAtPriceLabel;
              compare.hidden = !pricing.saving;
            }
            if (saving) {
              saving.textContent = pricing.savingLabel;
              saving.hidden = !pricing.saving;
            }
            if (unit) unit.textContent = pricing.unitLabel;
          }
        }
        if (!variants.some((variant) => matchesOtherOptions(variant, selected, config) && isConfiguredPackVariant(variant, count, config))) needsSetup = true;
      });
      const warning = qs('[data-hv-pack-setup-warning]', form);
      if (warning) warning.hidden = !needsSetup;
    };

    const showVariantImage = (image) => {
      if (!mainImage || !image) return;
      mainImage.src = image.src || '';
      if (image.srcset) mainImage.srcset = image.srcset;
      else mainImage.removeAttribute('srcset');
      mainImage.alt = image.alt || defaultImage?.alt || '';
      if (image.width) mainImage.width = image.width;
      if (image.height) mainImage.height = image.height;
      const matchingThumbnail = thumbnails.find((thumbnail) => String(thumbnail.dataset.hvProductImageId) === String(image.id));
      thumbnails.forEach((thumbnail) => {
        thumbnail.setAttribute('aria-current', String(thumbnail === matchingThumbnail));
      });
      if (galleryStatus && matchingThumbnail) {
        const index = thumbnails.indexOf(matchingThumbnail);
        galleryStatus.textContent = `Image ${index + 1} of ${thumbnails.length}`;
      }
    };

    const updateVariant = (shouldUpdateUrl) => {
      const variant = findSelectedVariant();
      const selectedCount = getSelectedPackCount();
      const pricing = getVariantPricing(variant, config, selectedCount);
      const available = isVariantPurchasable(variant, config, selectedCount);
      const compareWrap = qs('[data-hv-product-compare-wrap]', root);
      const availability = qs('[data-hv-product-availability]', root);
      const sku = qs('[data-hv-product-sku]', root);
      const skuWrap = qs('[data-hv-product-sku-wrap]', root);
      qsa('[data-hv-product-price]', root).forEach((label) => {
        label.textContent = pricing?.priceLabel || 'Unavailable';
      });
      qsa('[data-hv-product-compare]', root).forEach((label) => {
        label.textContent = pricing?.compareAtPriceLabel || '';
      });
      qsa('[data-hv-product-saved]', root).forEach((label) => {
        label.textContent = pricing?.savingLabel || '';
        label.hidden = !pricing?.hasCompare;
      });
      qsa('[data-hv-product-discount]', root).forEach((label) => {
        label.textContent = `${pricing?.discountPercent || 0}% OFF`;
        label.hidden = !pricing?.hasCompare;
      });
      qsa('[data-hv-product-pack-label]', root).forEach((label) => {
        label.textContent = pricing?.packLabel || 'Pack unavailable';
      });
      if (compareWrap) compareWrap.hidden = !pricing?.hasCompare;
      if (availability) availability.textContent = available ? 'Available' : config.enabled ? 'This pack is currently unavailable.' : 'This option is currently unavailable.';
      if (sku && skuWrap) {
        sku.textContent = variant?.sku || '';
        skuWrap.hidden = !variant?.sku;
      }

      if (quantity) quantity.value = quantityPackMode ? String(selectedCount) : '1';
      if (packProperties.size) packProperties.size.value = pricing?.packLabel || `${selectedCount} Pack${selectedCount === 1 ? '' : 's'}`;
      if (packProperties.total) packProperties.total.value = pricing?.priceLabel || '';
      if (packProperties.count) packProperties.count.value = String(selectedCount);
      refreshPackOptions(variant);

      if (variant) showVariantImage(variant.image || defaultImage);
      if (shouldUpdateUrl && variant) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        if (quantityPackMode) url.searchParams.set('pack', String(selectedCount));
        window.history.replaceState({}, '', url);
      }
    };

    variantInput.addEventListener('change', () => updateVariant(true));
    packInputs.forEach((input) => {
      input.addEventListener('change', () => {
        if (!input.checked) return;
        const count = Number(input.dataset.hvPackCount);
        const match = !input.disabled && Number(input.value) === count ? findPackVariant(variants, count, findSelectedVariant(), config) : null;
        if (!match) {
          updateVariant(false);
          announce('This pack is currently unavailable.');
          return;
        }
        variantInput.value = String(match.id);
        variantInput.dispatchEvent(new window.Event('change', { bubbles: true }));
      });
    });
    if (variantInput.tagName === 'SELECT') {
      Array.from(variantInput.options).forEach((option) => {
        const variant = variants.find((item) => String(item.id) === String(option.value));
        option.disabled = !isVariantPurchasable(variant, config, quantityPackMode ? getSelectedPackCount() : packCountForVariant(variant, config));
      });
    }
    if (variantField) {
      const onlyPackOption = config.packOptionIndex >= 0 && variants.every((variant) => variant.options?.length === 1);
      const onlyDefaultVariant = variants.every((variant) => !Array.isArray(variant.options) || variant.options.length <= 1);
      variantField.hidden = (quantityPackMode && onlyDefaultVariant) || (config.enabled && onlyPackOption);
    }
    if (quantityPackMode && packInputs.length) {
      const requestedPack = Number(new URL(window.location.href).searchParams.get('pack'));
      const requestedInput = packInputs.find((input) => Number(input.dataset.hvPackCount) === requestedPack && !input.disabled);
      if (requestedInput) requestedInput.checked = true;
    }
    updateVariant(false);
    if (packSelector) packSelector.hidden = false;
  };

  const initReviewVideos = (root) => {
    const cards = qsa('[data-hv-product-video]', root);
    const updateSoundBadge = (card, muted) => {
      const label = qs('[data-hv-video-sound-label]', card);
      const mutedIcon = qs('[data-hv-video-muted-icon]', card);
      const soundIcon = qs('[data-hv-video-sound-icon]', card);
      if (label) label.textContent = muted ? 'Tap for sound' : 'Sound on';
      if (mutedIcon) mutedIcon.toggleAttribute('hidden', !muted);
      if (soundIcon) soundIcon.toggleAttribute('hidden', muted);
      card.setAttribute('aria-label', muted ? 'Play customer review video with sound' : 'Mute customer review video');
    };
    cards.forEach((card) => {
      if (card.dataset.hvProductReady === 'true') return;
      card.dataset.hvProductReady = 'true';
      const video = qs('video', card);
      if (!video) return;
      video.play().catch(() => {});
      card.setAttribute('aria-pressed', 'false');
      updateSoundBadge(card, video.muted);
      card.addEventListener('click', () => {
        const nextMuted = !video.muted;
        cards.forEach((otherCard) => {
          const otherVideo = qs('video', otherCard);
          if (!otherVideo || otherCard === card) return;
          otherVideo.muted = true;
          otherCard.setAttribute('aria-pressed', 'false');
          updateSoundBadge(otherCard, true);
        });
        video.muted = nextMuted;
        if (!nextMuted) video.play().catch(() => {});
        card.setAttribute('aria-pressed', String(!nextMuted));
        updateSoundBadge(card, nextMuted);
        announce(nextMuted ? 'Review video muted.' : 'Review video sound on.');
      });
    });
  };

  const initLightbox = (root) => {
    const dialog = qs('[data-hv-product-lightbox]', root);
    const image = qs('[data-hv-product-lightbox-image]', dialog || root);
    const closeButton = qs('[data-hv-product-lightbox-close]', dialog || root);
    const triggers = qsa('[data-hv-product-review-image]', root);
    if (!dialog || !image || !closeButton || !triggers.length || dialog.dataset.hvProductReady === 'true') return;
    dialog.dataset.hvProductReady = 'true';

    let restoreFocus = null;
    const close = () => {
      if (dialog.open && typeof dialog.close === 'function') dialog.close();
      else dialog.removeAttribute('open');
      image.removeAttribute('src');
      if (restoreFocus) restoreFocus.focus();
    };
    const open = (trigger) => {
      restoreFocus = trigger;
      image.src = trigger.dataset.imageSrc || '';
      image.alt = trigger.dataset.imageAlt || '';
      if (typeof dialog.showModal === 'function') dialog.showModal();
      else dialog.setAttribute('open', '');
    };

    triggers.forEach((trigger) => trigger.addEventListener('click', () => open(trigger)));
    closeButton.addEventListener('click', close);
    dialog.addEventListener('click', (event) => {
      if (event.target === dialog) close();
    });
    dialog.addEventListener('cancel', (event) => {
      event.preventDefault();
      close();
    });
  };

  const initFaq = (root) => {
    const items = qsa('.hv-product-page__faq-item', root);
    items.forEach((item) => {
      if (item.dataset.hvProductReady === 'true') return;
      item.dataset.hvProductReady = 'true';
      item.addEventListener('toggle', () => {
        if (!item.open) return;
        items.forEach((other) => {
          if (other !== item) other.open = false;
        });
      });
    });
  };

  const init = (root) => {
    if (!root) return;
    if (root.dataset.hvProductReady === 'true') return;
    root.dataset.hvProductReady = 'true';
    initCountdown(root);
    initGallery(root);
    initOptionCarousel(root);
    initVariantForm(root);
    initReviewVideos(root);
    initLightbox(root);
    initFaq(root);
  };

  const initAll = (scope = document) => {
    const roots = scope?.matches?.('[data-hv-product-page]')
      ? [scope]
      : qsa('[data-hv-product-page]', scope);
    roots.forEach(init);
  };

  const destroyOptionCarousels = (scope = document) => {
    const carousels = scope?.matches?.('[data-hv-option-carousel]')
      ? [scope]
      : qsa('[data-hv-option-carousel]', scope);
    carousels.forEach((carousel) => optionCarouselInstances.get(carousel)?.destroy());
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initAll());
  else initAll();
  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    destroyOptionCarousels(event.target);
  });
})();
